import { useState } from "react";
import { api } from "@/lib/api";
import { useSciPreview } from "@/components/sci/useSciPreview";

function Lookup() {
  const [id, setId] = useState("");
  const [rec, setRec] = useState(null);
  const [err, setErr] = useState("");
  const go = () => api.get(`/admin/official-data/intl-earnings/record/${encodeURIComponent(id.trim())}`)
    .then((r) => { setRec(r.data); setErr(""); }).catch((e) => { setRec(null); setErr(e?.response?.data?.detail || "not found"); });
  return (
    <div className="mt-3">
      <div className="flex gap-2">
        <input data-testid="ie-lookup-input" value={id} onChange={(e) => setId(e.target.value)} placeholder="degree id, e.g. us-mit-scorecard-1107" className="w-80 rounded border border-stone-300 px-2 py-1 text-xs" />
        <button data-testid="ie-lookup-btn" onClick={go} className="rounded bg-stone-900 px-3 py-1 text-xs text-white">Look up</button>
      </div>
      {err && <div data-testid="ie-lookup-error" className="mt-1 text-xs text-red-600">{err}</div>}
      {rec && <pre data-testid="ie-lookup-result" className="mt-2 max-h-80 overflow-auto rounded bg-stone-50 p-2 text-[10px]">{JSON.stringify(rec, null, 1)}</pre>}
    </div>
  );
}

const CATS = ["retained", "relabelled", "refreshed", "added_official", "withheld"];

function Containment() {
  const { data: c } = useSciPreview("/admin/official-data/intl-earnings/report/containment");
  if (!c) return <p data-testid="ie-containment-missing" className="mt-3 text-xs text-stone-500">Containment report not built for this release.</p>;
  return (
    <details data-testid="ie-containment" className="mt-3 text-xs">
      <summary className="cursor-pointer font-medium">Containment report: unique course records by country</summary>
      <p className="mt-1 text-[11px] text-stone-500">A record can fall in more than one category (one field withheld, another refreshed), so category counts can add up to more than the unique total.</p>
      <table data-testid="ie-containment-table" className="mt-2 w-full text-left text-[11px]">
        <thead><tr className="text-stone-500"><th>Country</th><th className="text-right">Unique records</th>{CATS.map((k) => <th key={k} className="text-right">{k.replace("_", " ")}</th>)}<th className="text-right">Official programme figure</th><th className="text-right">Scoped context</th></tr></thead>
        <tbody>
          {Object.entries(c.degree_records).sort((a, b) => b[1].unique_records - a[1].unique_records).map(([k, v]) => (
            <tr key={k} data-testid={`ie-containment-row-${k}`} className="border-t border-stone-100">
              <td>{k}</td><td className="text-right">{v.unique_records.toLocaleString()}</td>
              {CATS.map((x) => <td key={x} className="text-right">{(v.records_by_category[x] || 0).toLocaleString()}</td>)}
              <td className="text-right">{v.records_with_official_programme_measure.toLocaleString()}</td><td className="text-right">{v.records_with_scoped_context.toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </details>
  );
}

function Shadow() {
  const { data: s } = useSciPreview("/admin/official-data/intl-earnings/report/shadow");
  if (!s) return <p data-testid="ie-shadow-missing" className="mt-3 text-xs text-stone-500">Shadow ranking report not built for this release.</p>;
  const changed = s.subject_rankings.filter((r) => r.published_before !== r.published_after || r.dropped || r.added || r.max_rank_move);
  return (
    <details data-testid="ie-shadow-report" className="mt-3 text-xs">
      <summary className="cursor-pointer font-medium">Shadow rankings: before vs after ({changed.length} of {s.subject_rankings.length} subject rankings change)</summary>
      <p className="mt-1 text-[11px] text-stone-500">{s.method}. Published rankings are not changed.</p>
      <p data-testid="ie-shadow-wua" className="mt-1 text-[11px]">WUA Outcomes Rank: {s.wua_ranked_before} ranked before, {s.wua_ranked_after} after; {s.wua_top20_kept}/20 of the top 20 unchanged.</p>
      <table data-testid="ie-shadow-table" className="mt-2 w-full text-left text-[11px]">
        <thead><tr className="text-stone-500"><th>Ranking</th><th>Published</th><th className="text-right">Ranked</th><th className="text-right">With salary</th><th className="text-right">Dropped / added</th><th className="text-right">Max move</th></tr></thead>
        <tbody>
          {changed.map((r) => (
            <tr key={r.ranking} className="border-t border-stone-100">
              <td>{r.ranking.replace("|", " · ")}</td><td>{r.published_before ? "yes" : "no"} → {r.published_after ? "yes" : "no"}</td>
              <td className="text-right">{r.ranked_before} → {r.ranked_after}</td><td className="text-right">{r.with_salary_before} → {r.with_salary_after}</td>
              <td className="text-right">{r.dropped} / {r.added}</td><td className="text-right">{r.max_rank_move ?? "–"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </details>
  );
}

export const IntlEarningsReview = () => {
  const { data, loading, error } = useSciPreview("/admin/official-data/intl-earnings");
  const a = data?.active;
  return (
    <section data-testid="di-intl-earnings" className="mt-10 rounded-lg border border-stone-200 bg-white p-5">
      <h2 className="font-chip text-lg font-semibold text-stone-900">International graduate earnings · resolver (preview)</h2>
      {loading ? <p data-testid="ie-loading" className="mt-2 text-sm text-stone-500">Loading release…</p> :
        error ? <p data-testid="ie-error" className="mt-2 text-sm text-red-700">Could not load the release summary.</p> :
        !a ? <p data-testid="ie-inactive" className="mt-2 text-sm text-stone-500">No active release.</p> : (
        <>
          <p data-testid="ie-summary" className="mt-1 text-xs text-stone-600">
            Release <code>{a.version}</code> · {a.records.toLocaleString()} course records · {a.universities.toLocaleString()} universities · served {String(data.served)} ·
            QA <span data-testid="ie-qa">{a.validation?.pass ? "passed" : "FAILED"}</span> · live approval {a.live_approval?.status}
          </p>
          <details className="mt-3 text-xs">
            <summary className="cursor-pointer font-medium">Decisions by country</summary>
            <table data-testid="ie-table" className="mt-2 w-full text-left text-[11px]">
              <thead><tr className="text-stone-500"><th>Country</th><th>Outcome</th><th className="text-right">Fields</th></tr></thead>
              <tbody>
                {Object.entries(a.stats || {}).flatMap(([c, st]) => Object.entries(st).map(([k, n]) => (
                  <tr key={`${c}-${k}`} className="border-t border-stone-100"><td>{c}</td><td className="font-mono">{k}</td><td className="text-right">{n.toLocaleString()}</td></tr>
                )))}
              </tbody>
            </table>
          </details>
          <details className="mt-3 text-xs">
            <summary className="cursor-pointer font-medium">Ranking inputs (shadow, preview only)</summary>
            <pre data-testid="ie-shadow" className="mt-1 text-[10px]">{JSON.stringify(a.shadow_ranking_inputs, null, 1)}</pre>
          </details>
          <Containment />
          <Shadow />
          <Lookup />
        </>
      )}
    </section>
  );
};
